import React from "react";
import { DataGridPro as MuiDataGridPro } from "@mui/x-data-grid-pro";

export * from "@mui/x-data-grid-pro";

const STORAGE_PREFIX = "dg-colvis";

const defaultGetRowId = (row) =>
  row?.id ??
  row?.ID ??
  row?.Id ??
  row?.Codice ??
  row?.codice ??
  row?.CODICE ??
  row?.UID ??
  row?.uuid ??
  row?._id;

const safeParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const hashString = (input) => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash << 5) - hash + input.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
};

const getStorageKey = ({ storageKey, columns }) => {
  if (storageKey) {
    return `${STORAGE_PREFIX}:${storageKey}`;
  }
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const fields = (columns || []).map((col) => col.field).join("|");
  return `${STORAGE_PREFIX}:${path}:${hashString(fields)}`;
};

export const DataGridPro = React.forwardRef((props, ref) => {
  const { columns, storageKey, columnVisibilityModel, initialState, getRowId } =
    props;

  const key = React.useMemo(
    () => getStorageKey({ storageKey, columns }),
    [storageKey, columns],
  );

  const isControlled = columnVisibilityModel != null;

  const [model, setModel] = React.useState(() => {
    if (isControlled) return columnVisibilityModel;
    if (typeof window === "undefined") return undefined;
    const stored = safeParse(window.localStorage.getItem(key));
    if (stored) return stored;
    return initialState?.columns?.columnVisibilityModel;
  });

  React.useEffect(() => {
    if (isControlled) {
      setModel(columnVisibilityModel);
    }
  }, [isControlled, columnVisibilityModel]);

  const handleChange = React.useCallback(
    (newModel) => {
      if (!isControlled) {
        setModel(newModel);
      }
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify(newModel));
      }
      if (props.onColumnVisibilityModelChange) {
        props.onColumnVisibilityModelChange(newModel);
      }
    },
    [isControlled, key, props],
  );

  const resolvedGetRowId = React.useCallback(
    (row) => {
      if (getRowId) {
        const value = getRowId(row);
        if (value !== undefined && value !== null) {
          return value;
        }
      }
      return defaultGetRowId(row);
    },
    [getRowId],
  );

  return (
    <MuiDataGridPro
      ref={ref}
      {...props}
      columnVisibilityModel={isControlled ? columnVisibilityModel : model}
      onColumnVisibilityModelChange={handleChange}
      getRowId={resolvedGetRowId}
    />
  );
});
